/**
 * Stepper component for the Indigo design system.
 *
 * A horizontal row of numbered steps: a number tile plus a label, with the
 * active step underlined. Each step is a button, so the stepper doubles as the
 * navigation between the steps of a flow. Pass `onStepChange` to let people
 * jump between steps; leave it out for a read-only progress indicator.
 *
 * `disabled` greys the whole row out — for a flow that has finished and can no
 * longer be revisited.
 *
 * @example
 * ```tsx
 * <Stepper
 *   steps={[
 *     { id: "details", label: "Details" },
 *     { id: "people", label: "People" },
 *     { id: "review", label: "Review" },
 *   ]}
 *   activeStepId={step}
 *   onStepChange={setStep}
 * />
 * ```
 */
import { clsx } from "clsx";
import type { ReactNode } from "react";
import "./Stepper.css";

export interface StepperStep {
  /** Stable id, reported back through `onStepChange`. */
  id: string;

  /** Text shown next to the step number. */
  label: ReactNode;

  /** Prevents moving to this step (e.g. until an earlier step is complete). */
  disabled?: boolean;
}

export interface StepperProps {
  /** Steps in order; the number tile shows each step's 1-based position. */
  steps: StepperStep[];

  /** Id of the current step. */
  activeStepId: string;

  /**
   * Called with a step's id when it is selected. Without it the steps are not
   * interactive and the stepper only reports progress.
   */
  onStepChange?: (stepId: string) => void;

  /** Greys out every step and blocks navigation (e.g. a completed flow). */
  disabled?: boolean;

  /** Accessible name for the step navigation. */
  ariaLabel?: string;

  /** Additional CSS classes applied to the root element. */
  className?: string;

  /** Test id applied to the root element. */
  "data-testid"?: string;
}

export const Stepper = ({
  steps,
  activeStepId,
  onStepChange,
  disabled = false,
  ariaLabel = "Steps",
  className,
  "data-testid": dataTestId,
}: StepperProps) => (
  <nav
    aria-label={ariaLabel}
    className={clsx(
      "indigo-stepper",
      disabled && "indigo-stepper--disabled",
      className,
    )}
    data-testid={dataTestId}
  >
    <ol className="indigo-stepper__list">
      {steps.map((step, index) => {
        const isActive = step.id === activeStepId;
        const isStepDisabled = disabled || step.disabled;
        const isInteractive = Boolean(onStepChange) && !isStepDisabled;

        return (
          <li key={step.id} className="indigo-stepper__item">
            <button
              type="button"
              className={clsx(
                "indigo-stepper__step",
                // The underline marks where the flow is; once the whole flow
                // is disabled there is no current step to point at.
                isActive && !disabled && "indigo-stepper__step--active",
                isStepDisabled && "indigo-stepper__step--disabled",
                isInteractive && "indigo-stepper__step--interactive",
              )}
              aria-current={isActive ? "step" : undefined}
              disabled={!isInteractive}
              onClick={() => onStepChange?.(step.id)}
            >
              <span className="indigo-stepper__number" aria-hidden="true">
                {index + 1}
              </span>
              <span className="indigo-stepper__label">{step.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  </nav>
);

Stepper.displayName = "Stepper";
